import { boolComparator, numberComparator, stringComparator } from '@utils/arrayUtils';

import { Comparator, TableColumn } from './types';

export type Order = 'asc' | 'desc';

export const NumericDefaultOrder: Order = 'desc';
export const TextDefaultOrder: Order = 'asc';

export function getDefaultColumnOrder<T>(column?: TableColumn<T>) {
  return column?.numeric ? NumericDefaultOrder : TextDefaultOrder;
}

export function numericComparator<T>(a: T, b: T, orderBy: keyof T) {
  return numberComparator(a[orderBy] as number, b[orderBy] as number);
}

export function booleanComparator<T>(a: T, b: T, orderBy: keyof T) {
  return boolComparator(a[orderBy] as boolean, b[orderBy] as boolean);
}

export function textComparator<T>(a: T, b: T, orderBy: keyof T) {
  return stringComparator(String(a[orderBy]), String(b[orderBy]));
}

export function getComparator<T>(
  order: Order,
  orderBy: keyof T,
  { comparator, numeric }: TableColumn<T> | undefined = {} as TableColumn<T>
): Comparator<T> {
  if (comparator)
    return order === 'desc' ? (a, b) => -comparator(a, b) : (a, b) => comparator(a, b);

  if (numeric)
    return order === 'desc'
      ? (a, b) => -numericComparator(a, b, orderBy)
      : (a, b) => numericComparator(a, b, orderBy);

  return order === 'desc'
    ? (a, b) => -textComparator(a, b, orderBy)
    : (a, b) => textComparator(a, b, orderBy);
}

export function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const tmp = array.map((el, index) => [el, index] as [T, number]);

  tmp.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  return tmp.map(el => el[0]);
}
